import type { ChangeEvent, FocusEvent } from 'react';
import { forwardRef } from 'react';
import { handleNativeInputEvent } from '../../utils/inputHandlers';
import type { InputDateProps } from '../InputDate';
import { InputDate } from '../InputDate';
import type { InputPasswordProps } from '../InputPassword';
import { InputPassword } from '../InputPassword';
import { InputText } from '../InputText';
import type { InputTextProps } from '../InputText';

interface BaseInputProps<T, V> {
  name: string;
  id?: string;
  type?: 'date' | 'password' | string;
  onFocus?: T extends never ? () => void : (e: FocusEvent<T>) => void;
  onBlur?: T extends never ? () => void : (e: FocusEvent<T>) => void;
  onChange: T extends never ? (value: V) => void : (value: V, e: ChangeEvent<T>) => void;
}

interface BaseInputDateProps extends BaseInputProps<never, string> {
  type: 'date';
}
interface BaseInputPasswordProps extends BaseInputProps<HTMLInputElement, string> {
  type: 'password';
}
interface BaseInputTextProps extends BaseInputProps<HTMLInputElement, string> {
  type?: string;
}

export type InputProps =
  | (BaseInputDateProps & InputDateProps)
  | (BaseInputPasswordProps & InputPasswordProps)
  | (BaseInputTextProps & InputTextProps);

export const Input = forwardRef(({ name, type, onChange, onFocus, onBlur, ...props }: InputProps, ref) => {
  switch (type) {
    case 'date':
      return (
        <InputDate
          onChange={onChange as BaseInputDateProps['onChange']}
          onFocus={onFocus as BaseInputDateProps['onFocus']}
          onBlur={onBlur as BaseInputDateProps['onBlur']}
          {...(props as any)}
        />
      );
    case 'password':
      return (
        <InputPassword
          name={name}
          onChange={handleNativeInputEvent(onChange)}
          onFocus={onFocus}
          onBlur={onBlur}
          {...(props as any)}
        />
      );
    default:
      return (
        <InputText
          ref={ref as any}
          type={type}
          name={name}
          onChange={handleNativeInputEvent(onChange)}
          onFocus={onFocus}
          onBlur={onBlur}
          {...props}
        />
      );
  }
});
